import { CalendarOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { Flex } from "antd";
import dayjs from "dayjs";
import { useMemo } from "react";
import { CustomiseIcon, SharedIcon } from "../../components/Icons/NoteIcons.component";
import { useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import {
  durationTimeFromType,
  getCategoryName,
  getClientDOB,
  getClientMRN,
  getGenderText,
  getLocationText,
  getNoteTypeName,
} from "../../utils/datamanipulation.utils";
import styles from "./notes.module.scss";

type TProps = {
  notesDetails: any;
};

// Re-export for backward compatibility
export const CUSTOMISE_ICON = <CustomiseIcon />;

const NotesHeader = ({ notesDetails }: TProps) => {
  const componentName = "notes";
  const isMobile = useIsMobileView();
  const { clients } = useAppSelector((state) => state.client);

  const mrn = useMemo(() => getClientMRN(clients, notesDetails?.client), [clients, notesDetails?.client]);
  const dob = useMemo(() => getClientDOB(clients, notesDetails?.client), [clients, notesDetails?.client]);
  const noteTypeName = useMemo(() => getNoteTypeName(notesDetails?.note_type_info), [notesDetails?.note_type_info]);

  return (
    <Flex vertical gap={12} className={styles[`${componentName}__detail-header`]}>
      {/* Top Row: Date/Time + Title (desktop) or just Date/Time (mobile) */}
      <div className={styles[`${componentName}__detail-header-top`]}>
        <div className={styles[`${componentName}__detail-header-top-left`]}>
          <CalendarOutlined style={{ fontSize: 16, color: "#6b7280" }} />
          <span>{dayjs(notesDetails?.uploaded_at).format("MMM D, YYYY, h:mm A")}</span>
          {!isMobile && (
            <>
              <span className={styles[`${componentName}__detail-header-top-left-separator`]}>•</span>
              <span className={styles[`${componentName}__detail-header-top-left-title`]}>
                {notesDetails?.title || "Untitled Session"}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Patient Name Row */}
      <div className={styles[`${componentName}__detail-header-patient`]}>
        <span className={styles[`${componentName}__detail-header-patient-name`]}>
          {notesDetails?.client_name || "No Patient"}
        </span>
        {getGenderText(notesDetails?.gender_type) && (
          <span className={styles[`${componentName}__detail-header-patient-gender`]}>
            ({getGenderText(notesDetails?.gender_type)})
          </span>
        )}
      </div>

      {/* Title Row (mobile only) */}
      {isMobile && (
        <div className={styles[`${componentName}__detail-header-title-mobile`]}>
          {notesDetails?.title || "Untitled Session"}
        </div>
      )}

      {/* Badge Row */}
      <div className={styles[`${componentName}__detail-header-badges`]}>
        {/* Note Type Badge */}
        {noteTypeName && (
          <span className={styles[`${componentName}__detail-header-badges-note-type`]}>Note Type: {noteTypeName}</span>
        )}
        {/* Category Badge */}
        {getCategoryName(notesDetails?.category_type) && (
          <span className={styles[`${componentName}__detail-header-badges-category`]}>
            {getCategoryName(notesDetails?.category_type)}
          </span>
        )}
        {/* Duration Badge */}
        {notesDetails?.duration_type !== undefined && notesDetails?.duration_type !== null && (
          <span className={styles[`${componentName}__detail-header-badges-duration`]}>
            <ClockCircleOutlined style={{ fontSize: 14 }} />
            {durationTimeFromType(notesDetails.duration_type)}
          </span>
        )}
        {/* Location Badge */}
        {notesDetails?.location_type !== undefined && notesDetails?.location_type !== null && (
          <span className={styles[`${componentName}__detail-header-badges-location`]}>
            {getLocationText(notesDetails?.location_type)}
          </span>
        )}
        {mrn && <span className={styles[`${componentName}__detail-header-badges-mrn`]}>MRN: {mrn}</span>}
        {dob && <span className={styles[`${componentName}__detail-header-badges-mrn`]}>DOB: {dob}</span>}
        {/* Shared Badge */}
        {!notesDetails?.is_owned && notesDetails?.is_owned !== undefined && notesDetails?.is_owned !== null && (
          <span className={styles[`${componentName}__detail-header-badges-shared`]}>
            <SharedIcon />
            Shared
          </span>
        )}
      </div>

      {/* Shared Note Owner Info */}
      {!notesDetails?.is_owned && notesDetails?.owner_name && (
        <div className={styles[`${componentName}__detail-header-owner`]}>
          Note created by: {notesDetails?.owner_name}
        </div>
      )}
    </Flex>
  );
};

export default NotesHeader;
